export interface TradingViewConfig {
  layout: string;
  charts: Chart[];
  symbolLock: number;
  intervalLock: number;
  trackTimeLock: number;
  dateRangeLock: number;
  crosshairLock: number;
  layoutsSizes: LayoutsSizes;
}

interface Chart {
  panes: Pane[];
  timeScale: TimeScale;
  chartProperties: ChartProperties;
  sessions: Sessions;
  version: number;
  timezone: string;
  shouldBeSavedEvenIfHidden: boolean;
  linkingGroup: null;
  lineToolsGroups: LineToolsGroups;
  chartId: string;
}

interface ChartProperties {
  paneProperties: PaneProperties;
  scalesProperties: ScalesProperties;
  chartEventsSourceProperties: ChartEventsSourceProperties;
  tradingProperties: TradingProperties;
  priceScaleSelectionStrategyName: string;
}

interface ChartEventsSourceProperties {
  visible: boolean;
  futureOnly: boolean;
  breaks: Breaks;
}

interface Breaks {
  color: string;
  visible?: boolean;
  style: number;
  width: number;
  transparency?: number;
  showPrice?: boolean;
}

interface PaneProperties {
  backgroundType: string;
  background: string;
  backgroundGradientStartColor: string;
  backgroundGradientEndColor: string;
  gridLinesMode: string;
  vertGridProperties: GridProperties;
  horzGridProperties: GridProperties;
  crossHairProperties: Breaks;
  topMargin: number;
  bottomMargin: number;
  axisProperties: AxisProperties;
  legendProperties: LegendProperties;
  separatorColor: string;
}

interface AxisProperties {
  autoScale: boolean;
  autoScaleDisabled: boolean;
  lockScale: boolean;
  percentage: boolean;
  percentageDisabled: boolean;
  log: boolean;
  logDisabled: boolean;
  alignLabels: boolean;
  isInverted: boolean;
  indexedTo100: boolean;
}

interface GridProperties {
  color: string;
}

interface LegendProperties {
  showStudyArguments: boolean;
  showStudyTitles: boolean;
  showStudyValues: boolean;
  showSeriesTitle: boolean;
  showSeriesOHLC: boolean;
  showLegend: boolean;
  showBarChange: boolean;
  showVolume: boolean;
  showBackground: boolean;
  showPriceSource: boolean;
  backgroundTransparency: number;
  showLogo: boolean;
}

interface ScalesProperties {
  backgroundColor: string;
  lineColor: string;
  textColor: string;
  fontSize: number;
  scaleSeriesOnly: boolean;
  showSeriesLastValue: boolean;
  seriesLastValueMode: number;
  showSeriesPrevCloseValue: boolean;
  showStudyLastValue: boolean;
  showSymbolLabels: boolean;
  showStudyPlotLabels: boolean;
  showBidAskLabels: boolean;
  showPrePostMarketPriceLabel: boolean;
  showFundamentalNameLabel: boolean;
  showFundamentalLastValue: boolean;
  barSpacing: number;
  axisHighlightColor: string;
  axisLineToolLabelBackgroundColorCommon: AxisLineToolLabelBackgroundColorCommon;
  axisLineToolLabelBackgroundColorActive: string;
  showPriceScaleCrosshairLabel: boolean;
  showTimeScaleCrosshairLabel: boolean;
  crosshairLabelBgColorLight: string;
  crosshairLabelBgColorDark: string;
}

enum AxisLineToolLabelBackgroundColorCommon {
  RGBA9113319103 = 'rgba(91, 133, 191, 0.3)',
  The00Bcd4 = '#00bcd4',
  The2962Ff = '#2962FF',
}

interface TradingProperties {
  showPositions: boolean;
  positionPL: Pl;
  bracketsPL: Pl;
  showOrders: boolean;
  showExecutions: boolean;
  showExecutionsLabels: boolean;
  showReverse: boolean;
  horizontalAlignment: number;
  extendLeft: boolean;
  lineLength: number;
  lineWidth: number;
  lineStyle: number;
}

interface Pl {
  visibility: boolean;
  display: number;
}

interface LineToolsGroups {
  groups: any[];
}

interface Pane {
  sources: Source[];
  mainSourceId: MainSourceID;
  stretchFactor: number;
  leftAxisesState: any[];
  rightAxisesState: RightAxisesState[];
  overlayPriceScales: OverlayPriceScales;
  priceScaleRatio: null;
}

enum MainSourceID {
  DvJF4V = 'DvJF4v',
  SeriesID = '_seriesId',
  The2QkLwR = '2QkLwR',
}

type OverlayPriceScales = object;

interface RightAxisesState {
  state: RightAxisesStateState;
  sources: string[];
}

interface RightAxisesStateState {
  id: string;
  m_priceRange: MPriceRange | null;
  m_isAutoScale: boolean;
  m_isPercentage: boolean;
  m_isIndexedTo100: boolean;
  m_isLog: boolean;
  m_isLockScale: boolean;
  m_isInverted: boolean;
  m_topMargin: number;
  m_bottomMargin: number;
  alignLabels: boolean;
  logFormula: LogFormula;
}

interface LogFormula {
  logicalOffset: number;
  coordOffset: number;
}

interface MPriceRange {
  m_maxValue: number;
  m_minValue: number;
}

interface Source {
  type: string;
  id: string;
  zorder: number;
  haStyle?: KagiStyleClass;
  renkoStyle?: KagiStyleClass;
  pbStyle?: KagiStyleClass;
  kagiStyle?: KagiStyleClass;
  pnfStyle?: KagiStyleClass;
  rangeStyle?: KagiStyleClass;
  formattingDeps?: FormattingDeps;
  state: SourceState;
  ownFirstValue?: null;
  metaInfo?: MetaInfo;
  points?: Point[];
  ownerSource?: MainSourceID;
  isSelectionEnabled?: boolean;
  userEditEnabled?: boolean;
  linkKey?: string;
  version?: number;
}

interface FormattingDeps {
  format: string;
  pricescale: number;
  minmov: number;
}

interface KagiStyleClass {
  studyId: string;
}

interface MetaInfo {
  palettes: MetaInfoPalettes;
  inputs: Input[];
  plots: Plot[];
  graphics: MetaInfoGraphics;
  defaults: Defaults;
  _metainfoVersion: number;
  id: string;
  description: string;
  shortDescription: string;
  is_price_study: boolean;
  format: Format;
  description_localized: string;
  shortId: string;
  packageId: string;
  version: string;
  fullId: string;
  productId: string;
  _serverMetaInfoVersion: number;
  isTVScript?: boolean;
  isTVScriptStub?: boolean;
  is_hidden_study?: boolean;
  styles?: MetaInfoStyles;
  scriptIdPart?: string;
  name?: string;
}

interface Defaults {
  graphics?: DefaultsGraphics;
  inputs: DefaultsInputs;
  styles?: DefaultsStyles;
  palettes?: DefaultsPalettes;
}

interface DefaultsGraphics {
  horizlines?: PurpleHorizlines;
}

interface PurpleHorizlines {
  highest: Breaks;
  lowest: Breaks;
}

interface DefaultsInputs {
  highestSource?: string;
  lowestSource?: string;
}

interface DefaultsPalettes {
  palette_0?: PurplePalette0;
}

interface PurplePalette0 {
  colors: { [key: string]: Breaks };
}

interface DefaultsStyles {
  plot_0?: PurplePlot0;
}

interface PurplePlot0 {
  display: number;
  linestyle: number;
  linewidth: number;
  plottype: number;
  trackPrice: boolean;
  transparency: number;
  color: string;
  title?: string;
}

interface Format {
  type: string;
}

interface MetaInfoGraphics {
  horizlines?: FluffyHorizlines;
}

interface FluffyHorizlines {
  highest: Highest;
  lowest: Highest;
}

interface Highest {
  name: string;
}

interface Input {
  defval: string;
  id: string;
  name: string;
  options: string[];
  type: string;
  display: number;
}

interface MetaInfoPalettes {
  palette_0?: FluffyPalette0;
}

interface FluffyPalette0 {
  colors: { [key: string]: Highest };
}

interface Plot {
  id: string;
  type: string;
  palette?: string;
  target?: string;
}

interface MetaInfoStyles {
  plot_0: FluffyPlot0;
}

interface FluffyPlot0 {
  title: string;
  histogramBase: number;
  joinPoints: boolean;
}

interface Point {
  price: number;
  offset: number;
  time_t: number;
}

interface SourceState {
  style?: number;
  esdShowDividends?: boolean;
  esdShowSplits?: boolean;
  esdShowEarnings?: boolean;
  esdShowBreaks?: boolean;
  esdFlagSize?: number;
  showContinuousContractSwitches?: boolean;
  showContinuousContractSwitchesBreaks?: boolean;
  showFuturesContractExpiration?: boolean;
  showLastNews?: boolean;
  showCountdown?: boolean;
  bidAsk?: BidAsk;
  prePostMarket?: PrePostMarket;
  highLowAvgPrice?: HighLowAvgPrice;
  visible: boolean;
  showPriceLine?: boolean;
  priceLineWidth?: number;
  priceLineColor?: string;
  baseLineColor?: string;
  showPrevClosePriceLine?: boolean;
  prevClosePriceLineWidth?: number;
  prevClosePriceLineColor?: string;
  minTick?: string;
  dividendsAdjustment?: OverlayPriceScales;
  backAdjustment?: boolean;
  settlementAsClose?: boolean;
  sessionId?: string;
  sessVis?: boolean;
  statusViewStyle?: StatusViewStyle;
  candleStyle?: CandleStyleClass;
  hollowCandleStyle?: CandleStyleClass;
  haStyle?: CandleStyleClass;
  barStyle?: BarStyle;
  hiloStyle?: HiloStyle;
  columnStyle?: ColumnStyle;
  lineStyle?: LineStyle;
  lineWithMarkersStyle?: LineStyle;
  steplineStyle?: LineStyle;
  areaStyle?: AreaStyle;
  hlcAreaStyle?: HlcAreaStyle;
  renkoStyle?: RenkoStyle;
  pbStyle?: PbStyle;
  kagiStyle?: KagiStyle;
  pnfStyle?: PnfStyle;
  baselineStyle?: BaselineStyle;
  rangeStyle?: RangeStyle;
  symbol?: string;
  shortName?: string;
  timeframe?: string;
  onWidget?: boolean;
  interval?: Interval;
  unitId?: null;
  currencyId?: null | string;
  showSessions?: boolean;
  priceAxisProperties?: AxisProperties;
  graphics?: DefaultsGraphics;
  inputs?: DefaultsInputs;
  precision?: string;
  styles?: DefaultsStyles;
  bands?: OverlayPriceScales;
  ohlcPlots?: OverlayPriceScales;
  palettes?: DefaultsPalettes;
  filledAreasStyle?: OverlayPriceScales;
  filledAreas?: OverlayPriceScales;
  showLegendValues?: boolean;
  showLabelsOnPriceScale?: boolean;
  parentSources?: OverlayPriceScales;
  intervalsVisibilities?: IntervalsVisibilities;
  linecolor?: Linecolor;
  linewidth?: number;
  linestyle?: number;
  smooth?: number;
  fillBackground?: boolean;
  backgroundColor?: AxisLineToolLabelBackgroundColorCommon;
  transparency?: number;
  leftEnd?: number;
  rightEnd?: number;
  title?: string;
  frozen?: boolean;
  zOrderVersion?: number;
  symbolStateVersion?: number;
  color?: AxisLineToolLabelBackgroundColorCommon;
  textcolor?: string;
  fontsize?: number;
  bold?: boolean;
  italic?: boolean;
  showCoeffs?: boolean;
  showPrices?: boolean;
  extendLines?: boolean;
  extendLinesLeft?: boolean;
  horzLabelsAlign?: string;
  vertLabelsAlign?: string;
  reverse?: boolean;
  coeffsAsPercents?: boolean;
  fibLevelsBasedOnLogScale?: boolean;
  labelFontSize?: number;
  trendline?: LineStyle;
  levelsStyle?: LevelsStyle;
  level1?: Array<boolean | Linecolor | number>;
  level2?: Array<boolean | number | string>;
  level3?: Array<boolean | number | string>;
  level4?: Array<boolean | number | string>;
  level5?: Array<boolean | number | string>;
  level6?: Array<boolean | number | AxisLineToolLabelBackgroundColorCommon>;
  level7?: Array<boolean | Linecolor | number>;
  level8?: Array<boolean | number | AxisLineToolLabelBackgroundColorCommon>;
  level9?: Array<boolean | number | string>;
  level10?: Array<boolean | number | string>;
  level11?: Array<boolean | number | string>;
  level12?: Array<boolean | number | string>;
  level13?: Array<boolean | number | string>;
  level16?: Array<boolean | number | string>;
  level14?: Array<boolean | number | string>;
  level15?: Array<boolean | number | string>;
  level17?: Array<boolean | AxisLineToolLabelBackgroundColorCommon | number>;
  level18?: Array<boolean | number | Linecolor>;
  level19?: Array<boolean | number | AxisLineToolLabelBackgroundColorCommon>;
  level20?: Array<boolean | number | string>;
  level21?: Array<boolean | number | string>;
  level22?: Array<boolean | number | string>;
  level23?: Array<boolean | number | string>;
  level24?: Array<boolean | number | string>;
  backgroundTransparency?: number;
  drawBorder?: boolean;
  borderColor?: string;
  fixedSize?: boolean;
  wordWrap?: boolean;
  wordWrapWidth?: number;
  text?: string;
  fillLabelBackground?: boolean;
  labelBackgroundColor?: string;
  stopBackground?: string;
  profitBackground?: string;
  stopBackgroundTransparency?: number;
  profitBackgroundTransparency?: number;
  compact?: boolean;
  riskDisplayMode?: string;
  accountSize?: number;
  lotSize?: number;
  risk?: number;
  alwaysShowStats?: boolean;
  showPriceLabels?: boolean;
  stopLevel?: number;
  profitLevel?: number;
  riskSize?: number;
  qty?: number;
  amountTarget?: number;
  amountStop?: number;
  size?: number;
  emoji?: string;
  angle?: number;
  extendLeft?: boolean;
  extendRight?: boolean;
  showLabel?: boolean;
  showMiddlePoint?: boolean;
  showPriceRange?: boolean;
  showPercentPriceRange?: boolean;
  showPipsPriceRange?: boolean;
  showBarsRange?: boolean;
  showDateTimeRange?: boolean;
  showDistance?: boolean;
  showAngle?: boolean;
  statsPosition?: number;
}

interface AreaStyle {
  color1: string;
  color2: AxisLineToolLabelBackgroundColorCommon;
  linecolor: AxisLineToolLabelBackgroundColorCommon;
  linestyle: number;
  linewidth: number;
  priceSource: string;
  transparency: number;
}

interface BarStyle {
  upColor: string;
  downColor: string;
  barColorsOnPrevClose: boolean;
  dontDrawOpen: boolean;
  thinBars: boolean;
}

interface BaselineStyle {
  baselineColor: string;
  topFillColor1: string;
  topFillColor2: string;
  bottomFillColor1: string;
  bottomFillColor2: string;
  topLineColor: string;
  bottomLineColor: string;
  topLineWidth: number;
  bottomLineWidth: number;
  priceSource: string;
  transparency: number;
  baseLevelPercentage: number;
}

interface BidAsk {
  visible: boolean;
  lineStyle: number;
  lineWidth: number;
  bidLineColor: AxisLineToolLabelBackgroundColorCommon;
  askLineColor: string;
}

interface CandleStyleClass {
  upColor: string;
  downColor: string;
  drawWick: boolean;
  drawBorder: boolean;
  borderColor: string;
  borderUpColor: string;
  borderDownColor: string;
  wickColor: string;
  wickUpColor: string;
  wickDownColor: string;
  barColorsOnPrevClose?: boolean;
  drawBody: boolean;
  showRealLastPrice?: boolean;
  inputs?: OverlayPriceScales;
  inputInfo?: OverlayPriceScales;
}

interface ColumnStyle {
  upColor: string;
  downColor: string;
  barColorsOnPrevClose: boolean;
  priceSource: string;
}

interface HighLowAvgPrice {
  highLowPriceLinesVisible: boolean;
  highLowPriceLabelsVisible: boolean;
  averageClosePriceLineVisible: boolean;
  averageClosePriceLabelVisible: boolean;
  highLowPriceLinesColor: string;
  highLowPriceLinesWidth: number;
  averagePriceLineColor: string;
  averagePriceLineWidth: number;
}

interface HiloStyle {
  color: AxisLineToolLabelBackgroundColorCommon;
  showBorders: boolean;
  borderColor: AxisLineToolLabelBackgroundColorCommon;
  showLabels: boolean;
  labelColor: AxisLineToolLabelBackgroundColorCommon;
  drawBody: boolean;
}

interface HlcAreaStyle {
  highLineColor: string;
  highLineStyle: number;
  highLineWidth: number;
  lowLineColor: string;
  lowLineStyle: number;
  lowLineWidth: number;
  closeLineColor: string;
  closeLineStyle: number;
  closeLineWidth: number;
  highCloseFillColor: string;
  closeLowFillColor: string;
}

enum Interval {
  D = 'D',
}

interface IntervalsVisibilities {
  ticks: boolean;
  seconds: boolean;
  secondsFrom: number;
  secondsTo: number;
  minutes: boolean;
  minutesFrom: number;
  minutesTo: number;
  hours: boolean;
  hoursFrom: number;
  hoursTo: number;
  days: boolean;
  daysFrom: number;
  daysTo: number;
  weeks: boolean;
  weeksFrom: number;
  weeksTo: number;
  months: boolean;
  monthsFrom: number;
  monthsTo: number;
  ranges: boolean;
}

interface KagiStyle {
  upColor: string;
  downColor: string;
  upColorProjection: string;
  downColorProjection: string;
  inputs: KagiStyleInputs;
  inputInfo: KagiStyleInputInfo;
}

interface KagiStyleInputInfo {
  source: Highest;
  style: Highest;
  atrLength: Highest;
  reversalAmount: Highest;
}

interface KagiStyleInputs {
  source: string;
  style: string;
  atrLength: number;
  reversalAmount: number;
}

enum Linecolor {
  The00Bcd4 = '#00bcd4',
  The2962Ff = '#2962FF',
  The787B86 = '#787B86',
}

interface LevelsStyle {
  linewidth: number;
  linestyle: number;
}

interface LineStyle {
  color: Linecolor;
  linestyle: number;
  linewidth: number;
  priceSource?: string;
  visible?: boolean;
}

interface PbStyle {
  upColor: string;
  downColor: string;
  borderUpColor: string;
  borderDownColor: string;
  upColorProjection: string;
  downColorProjection: string;
  borderUpColorProjection: string;
  borderDownColorProjection: string;
  inputs: PbStyleInputs;
  inputInfo: PbStyleInputInfo;
}

interface PbStyleInputInfo {
  source: Highest;
  lb: Highest;
}

interface PbStyleInputs {
  source: string;
  lb: number;
}

interface PnfStyle {
  upColor: string;
  downColor: string;
  upColorProjection: string;
  downColorProjection: string;
  inputs: PnfStyleInputs;
  inputInfo: PnfStyleInputInfo;
}

interface PnfStyleInputInfo {
  sources: Highest;
  boxSize: Highest;
  reversalAmount: Highest;
  style: Highest;
  atrLength: Highest;
  oneStepBackBuilding: Highest;
}

interface PnfStyleInputs {
  sources: string;
  reversalAmount: number;
  boxSize: number;
  style: string;
  atrLength: number;
  oneStepBackBuilding: boolean;
}

interface PrePostMarket {
  visible: boolean;
  lineStyle: number;
  lineWidth: number;
  preMarketColor: string;
  postMarketColor: AxisLineToolLabelBackgroundColorCommon;
}

interface RangeStyle {
  barStyle: number;
  upColor: string;
  downColor: string;
  upColorProjection: string;
  downColorProjection: string;
  thinBars: boolean;
  candlesUpColor: string;
  candlesDownColor: string;
  candlesBorderUpColor: string;
  candlesBorderDownColor: string;
  candlesWickUpColor: string;
  candlesWickDownColor: string;
  inputs: RangeStyleInputs;
  inputInfo: RangeStyleInputInfo;
}

interface RangeStyleInputInfo {
  range: Highest;
  phantomBars: Highest;
}

interface RangeStyleInputs {
  range: number;
  phantomBars: boolean;
}

interface RenkoStyle {
  upColor: string;
  downColor: string;
  borderUpColor: string;
  borderDownColor: string;
  upColorProjection: string;
  downColorProjection: string;
  borderUpColorProjection: string;
  borderDownColorProjection: string;
  wickUpColor: string;
  wickDownColor: string;
  inputs: RenkoStyleInputs;
  inputInfo: RenkoStyleInputInfo;
}

interface RenkoStyleInputInfo {
  source: Highest;
  sources: Highest;
  boxSize: Highest;
  style: Highest;
  atrLength: Highest;
  wicks: Highest;
}

interface RenkoStyleInputs {
  source: string;
  sources: string;
  boxSize: number;
  style: string;
  atrLength: number;
  wicks: boolean;
}

interface StatusViewStyle {
  fontSize: number;
  showExchange: boolean;
  showInterval: boolean;
  symbolTextSource: string;
}

interface Sessions {
  properties: Properties;
}

interface Properties {
  graphics: PropertiesGraphics;
}

interface PropertiesGraphics {
  backgrounds: Backgrounds;
  vertlines: Vertlines;
}

interface Backgrounds {
  outOfSession: OutOfSession;
  preMarket: OutOfSession;
  postMarket: OutOfSession;
}

interface OutOfSession {
  color: string;
  transparency: number;
  visible: boolean;
}

interface Vertlines {
  sessBreaks: Breaks;
}

interface TimeScale {
  m_barSpacing: number;
  m_rightOffset: number;
  rightOffsetPercentage: number;
  usePercentageRightOffset: boolean;
}

interface LayoutsSizes {
  s: Empty[];
}

interface Empty {
  percent: number;
}
